import React, { useMemo } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import useSWR from 'swr';
import { ReportView } from '../../../components/ui';
import { useTheme } from '../../../contexts/ThemeContext';
import { cn } from '../../../lib/utils';
import { fetcher } from '../../../services/fetcher';
import type { CrewStatisticsResponse, CrewStatsOnboardRow } from '../types';
import { OnboardCountReport } from './OnboardCountReport';
import { onboardCountBackPath, onboardCountListApiUrl } from './onboardCountPrintMeta';

const OnboardCountReportPage: React.FC = () => {
  const { colors } = useTheme();
  const [searchParams] = useSearchParams();

  const listUrl = useMemo(() => onboardCountListApiUrl(searchParams), [searchParams]);
  const { data, error, isLoading } = useSWR<CrewStatisticsResponse>(listUrl, fetcher, {
    revalidateOnFocus: false,
  });

  const vesselId = searchParams.get('vesselId')?.trim() ?? '';
  const onboard = useMemo(
    () => (data?.lists?.onboard ?? []) as CrewStatsOnboardRow[],
    [data?.lists?.onboard],
  );

  const vesselLabel = useMemo(() => {
    if (data?.vessel?.vesselName) return data.vessel.vesselName;
    if (!vesselId) return 'All vessels';
    return `Vessel #${vesselId}`;
  }, [data?.vessel?.vesselName, vesselId]);

  const backTo = onboardCountBackPath();

  if (isLoading) {
    return (
      <div className={cn('flex min-h-[40vh] items-center justify-center text-sm', colors.textMuted)}>
        Loading onboard counts…
      </div>
    );
  }

  if (error || !data) {
    return (
      <div
        className={cn(
          'flex min-h-[40vh] flex-col items-center justify-center gap-3 px-4',
          colors.background,
        )}
      >
        <p className={cn('text-sm', colors.textMuted)}>Could not load onboard statistics.</p>
        <Link to={backTo} className={cn('text-sm font-medium hover:underline', colors.primaryFg)}>
          ← Back to Onboard
        </Link>
      </div>
    );
  }

  return (
    <ReportView
      backTo={backTo}
      backLabel="Back to Onboard"
      title="Onboard counts"
      subtitle={`${vesselLabel} · ${onboard.length} crew`}
      printPageSize="A4 landscape"
      printMargin="0"
    >
      <OnboardCountReport onboard={onboard} vesselLabel={vesselLabel} />
    </ReportView>
  );
};

export default OnboardCountReportPage;
